import type { Workspace, WidgetContainer, Layout } from '@thinkalpha/ui-types/contracts/workspace';
import { createTabFromViewModel } from 'src/lib/widget/createTabFromViewModel';
import type {
    WorkspaceViewModel,
    ContainerViewModel,
    WidgetTabViewModel,
    ContainerLayoutViewModel,
} from 'src/store/types';

/**
 * Creates a Layout from a ContainerLayoutViewModel
 */
export function createLayoutFromViewModel(layout: ContainerLayoutViewModel): Layout {
    return {
        ...layout,
        ...(layout.screen ? { screen: layout.screen } : {}),
    };
}

/**
 * Creates a WidgetContainer from its view model components
 */
export function createContainerFromViewModel(
    normalizedContainer: ContainerViewModel,
    normalizedWidgetTabs: WidgetTabViewModel[],
    normalizedLayout: ContainerLayoutViewModel,
): WidgetContainer {
    return {
        id: normalizedContainer.id,
        activeTabId: normalizedContainer.activeTabId,
        layout: createLayoutFromViewModel(normalizedLayout),
        tabs: normalizedWidgetTabs.map((tab) => createTabFromViewModel(tab)),
    };
}

/**
 * Creates a complete Workspace from view model components,
 * using the new widget converter functions for the widget portions.
 */
export function createWorkspaceFromViewModels(
    workspace: WorkspaceViewModel,
    containers: ContainerViewModel[],
    widgetTabs: WidgetTabViewModel[],
    layouts: ContainerLayoutViewModel[],
): Workspace {
    return {
        id: workspace.id,
        name: workspace.name,
        permissions: workspace.permissions,
        isTemplate: workspace.isTemplate,
        description: workspace.description,
        maximizedContainerId: workspace.maximizedContainerId,
        containers: containers.map((container) =>
            createContainerFromViewModel(
                container,
                widgetTabs.filter((tab) => tab.containerId === container.id),
                layouts.find((x) => x.containerId === container.id)!,
            ),
        ),
        layoutSettings: workspace.layoutSettings,
        activeBorderColor: workspace.activeBorderColor,
    };
}

export function createWorkspaceFromContainers(workspace: WorkspaceViewModel, containers: WidgetContainer[]): Workspace {
    return {
        id: workspace.id,
        name: workspace.name,
        permissions: workspace.permissions,
        isTemplate: workspace.isTemplate,
        description: workspace.description,
        maximizedContainerId: workspace.maximizedContainerId,
        containers,
        layoutSettings: workspace.layoutSettings,
        activeBorderColor: workspace.activeBorderColor,
    };
}
